import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Plus, RefreshCw } from "lucide-react";

import {
  beginGoogleCalendarConnect,
  disconnectGoogleCalendar,
  listCalendarSubscriptions,
  readGoogleCalendarStatus,
  type CalendarSubscription,
  type GoogleCalendarConnectionStatus,
} from "~/neucleos/calendarClient";
import { connectPermissionRefusalCopy, isToolNotOfferedRefusal } from "~/neucleos/refusalCopy";
import { openWorldViewCalendar } from "~/neucleos/worldViewController";
import { useNeucleosWorldStore } from "~/neucleos/worldStore";
import { listEmailAccounts, type EmailAccountSummary } from "~/neucleos/commsClient";

import { COCKPIT_ORIGIN } from "~/neucleos/cockpitOrigin";
/**
 * CN1 — the World's integrations in one Connections home, composed from
 * the integrations-1 card grammar and the integrations-6 health strip.
 * Every card wears an honest state: Google Calendar reads and disconnects
 * through the governed lane and CONNECTS only via the cockpit consent
 * door (a real browser ceremony, never a background call); subscription
 * health folds from the authority's own fetch stamps; the outbound feed
 * doors into the Calendar surface. Machine/session custody stays its own
 * section below — distinct kinds, never merged.
 */

export const COCKPIT_CONNECT_ORIGIN = COCKPIT_ORIGIN;

// The catalog blocks' own grammar (integrations-1 cards, integrations-6
// frame/panel strip), verbatim — the ncl-reactbits scope resolves every
// neutral-* and --rb-* token to the estate contract.
const rbFocus =
  "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--rb-accent,oklch(20.5%_0_0))] dark:focus-visible:outline-[var(--rb-accent,oklch(100%_0_0))]";
const rbTransition =
  "transition-[background-color,border-color,color,opacity,transform] duration-150 ease-out";
const rbFrame =
  "rounded-[var(--rb-r-2xl,14px)] border border-neutral-200/70 bg-neutral-50 p-1 dark:border-neutral-800 dark:bg-neutral-950";
const rbPanel =
  "rounded-[var(--rb-r-lg,10px)] border border-neutral-200/70 bg-white dark:border-neutral-800 dark:bg-neutral-900";
const rbSecondaryButton = `inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-[var(--rb-r-md,8px)] border border-neutral-200 bg-white px-3 text-[13px] text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-300 dark:hover:bg-neutral-800 ${rbTransition} ${rbFocus}`;
const rbAccentButton = `inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-[var(--rb-r-md,8px)] bg-[var(--rb-accent,oklch(20.5%_0_0))] px-3 text-[13px] text-[var(--rb-accent-fg,oklch(100%_0_0))] hover:bg-[color-mix(in_oklab,var(--rb-accent,oklch(20.5%_0_0))_90%,transparent)] dark:bg-[var(--rb-accent,oklch(100%_0_0))] dark:text-[var(--rb-accent-fg,oklch(20.5%_0_0))] dark:hover:bg-[color-mix(in_oklab,var(--rb-accent,oklch(100%_0_0))_90%,transparent)] ${rbTransition} ${rbFocus}`;

export type IntegrationGoogleState =
  | { readonly kind: "loading" }
  | ({ readonly kind: "ready" } & GoogleCalendarConnectionStatus)
  | { readonly kind: "refusal"; readonly refusalKind: string; readonly reason: string };

export type IntegrationSubscriptionsState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly feeds: ReadonlyArray<CalendarSubscription> }
  | { readonly kind: "refusal"; readonly refusalKind: string; readonly reason: string };

export type IntegrationGmailState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly accounts: ReadonlyArray<EmailAccountSummary> }
  | { readonly kind: "refusal"; readonly refusalKind: string; readonly reason: string };

/**
 * 7 Oct 2026 (the principal, installed 0.0.193): the Google row's own
 * action standing — a pending Connect or Disconnect, the reason one was
 * refused, and that a Connect is waiting in the browser — kept beside the
 * status read so a re-read never erases it.
 */
export interface IntegrationGoogleControl {
  readonly pending: "connect" | "disconnect" | null;
  readonly error: string | null;
  readonly connectStarted: boolean;
}

export const IDLE_GOOGLE_CONTROL: IntegrationGoogleControl = {
  pending: null,
  error: null,
  connectStarted: false,
};

/** While a disconnect is revoking at Google, the row re-reads this often, this many times. */
export const GOOGLE_REVOKE_POLL_MS = 2_000;
export const GOOGLE_REVOKE_POLL_LIMIT = 15;

export type WorldIntegrationsCommand =
  | { readonly type: "connectGoogle" }
  | { readonly type: "disconnectGoogle" }
  | { readonly type: "openCalendar" }
  | { readonly type: "refreshIntegrations" };

const TOOL_NOT_OFFERED_LINE =
  "This World doesn't offer this integration yet. It will appear when the World is updated; you don't need to sign in again.";

function refusalLine(refusalKind: string, reason: string): string {
  return isToolNotOfferedRefusal(refusalKind) ? TOOL_NOT_OFFERED_LINE : reason;
}

/** A refused Connect: a missing World permission reads as one plain sentence, never its code. */
function connectRefusalLine(integration: string, refusalKind: string, reason: string): string {
  return connectPermissionRefusalCopy(integration, reason) ?? refusalLine(refusalKind, reason);
}

function agoLabel(at: number | null): string {
  if (at === null) return "never fetched";
  const minutes = Math.max(0, Math.round((Date.now() - at) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

function Card({
  mark,
  name,
  group,
  blurb,
  children,
  integration,
}: {
  readonly mark: string;
  readonly name: string;
  readonly group: string;
  readonly blurb: string;
  readonly children: React.ReactNode;
  readonly integration: string;
}) {
  return (
    <article
      className="flex flex-col rounded-[var(--rb-r-2xl,14px)] border border-neutral-200/70 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900"
      data-cleo="integration-card"
      data-integration={integration}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--rb-r-lg,10px)] border border-neutral-200/70 bg-white text-[12px] text-neutral-600 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-300"
        >
          {mark}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[14px] font-medium text-neutral-900 dark:text-white">
            {name}
          </h3>
          <p className="mt-0.5 text-[12px] text-neutral-500 dark:text-neutral-400">{group}</p>
        </div>
      </div>
      <p className="mt-3 flex-1 text-[13px] leading-5 text-neutral-600 dark:text-neutral-400">
        {blurb}
      </p>
      <div className="mt-4">{children}</div>
    </article>
  );
}

export function WorldIntegrationsView({
  worldKey,
  google,
  googleControl = IDLE_GOOGLE_CONTROL,
  gmail,
  subscriptions,
  onCommand,
}: {
  readonly worldKey: string;
  readonly google: IntegrationGoogleState;
  readonly googleControl?: IntegrationGoogleControl;
  readonly gmail: IntegrationGmailState;
  readonly subscriptions: IntegrationSubscriptionsState;
  readonly onCommand: (command: WorldIntegrationsCommand) => void;
}) {
  const feeds = subscriptions.kind === "ready" ? subscriptions.feeds : [];
  const gmailAccounts = gmail.kind === "ready" ? gmail.accounts : [];
  const gmailConnected = gmailAccounts.some((account) => account.status === "active");
  const gmailNeedsReconnect = gmailAccounts.some((account) => account.status !== "active");
  // 7 Oct 2026 (the principal, installed 0.0.193): the strip read "Healthy
  // 1, Failed 0" while Google Calendar sat on "Disconnecting…". A disconnect
  // that has not finished (in flight, stuck, or refused by Google) is the
  // person's own unfinished action, and one that finished without Google's
  // confirmation may leave the grant live at Google: both need attention.
  const googleNeedsAttention =
    google.kind === "ready" &&
    (google.status === "revoking" ||
      (google.status === "disconnected" && google.revokeError !== null));
  const healthy =
    feeds.filter((feed) => feed.lastFetchError === null).length +
    (google.kind === "ready" && google.status === "active" ? 1 : 0) +
    (gmailConnected ? 1 : 0);
  const needsAttention =
    feeds.filter((feed) => feed.lastFetchError !== null).length +
    (gmailNeedsReconnect ? 1 : 0) +
    (googleNeedsAttention ? 1 : 0);

  return (
    <section className="ncl-reactbits flex flex-col gap-4" data-cleo="integrations">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[17px] font-medium tracking-[-0.01em] text-neutral-900 dark:text-white">
            Integrations
          </h2>
          <p className="mt-1 text-[13px] text-neutral-500 dark:text-neutral-400">
            What this World is connected to, with its honest standing.
          </p>
        </div>
        <button
          className={`${rbSecondaryButton} h-8`}
          data-cleo="integrations-refresh"
          onClick={() => onCommand({ type: "refreshIntegrations" })}
          type="button"
        >
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.75} />
          Refresh
        </button>
      </div>

      <div
        className={`${rbFrame} grid grid-cols-2 gap-1 sm:grid-cols-4`}
        data-cleo="integration-health"
      >
        <div className={`${rbPanel} px-3 py-2.5`}>
          <p className="text-[12px] text-neutral-500 dark:text-neutral-400">Healthy</p>
          <p
            className="mt-0.5 text-[20px] leading-none tabular-nums text-emerald-600 dark:text-emerald-500"
            data-cleo="integration-health-healthy"
          >
            {healthy}
          </p>
        </div>
        <div className={`${rbPanel} px-3 py-2.5`}>
          <p className="text-[12px] text-neutral-500 dark:text-neutral-400">Needs attention</p>
          <p
            className="mt-0.5 text-[20px] leading-none tabular-nums text-red-600 dark:text-red-400"
            data-cleo="integration-health-failed"
          >
            {needsAttention}
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Card
          blurb="Your bookings appear in your own Google calendar. Two narrow event scopes; disconnect any time."
          group="Calendar"
          integration="google"
          mark="GC"
          name="Google Calendar"
        >
          {google.kind === "loading" ? (
            <p className="text-[13px] text-neutral-500 dark:text-neutral-400">Reading standing…</p>
          ) : google.kind === "refusal" ? (
            <p
              className="text-[12px] leading-5 text-red-600 dark:text-red-400"
              data-cleo="integration-refusal"
            >
              {refusalLine(google.refusalKind, google.reason)}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {google.status === "active" ? (
                <>
                  <p
                    className="flex min-w-0 items-center gap-1.5 text-[13px] text-emerald-600 dark:text-emerald-500"
                    data-cleo="google-connected"
                  >
                    <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                    {google.googleEmail !== null ? (
                      <span className="truncate">
                        Connected as <span data-cleo="google-account">{google.googleEmail}</span>
                      </span>
                    ) : (
                      "Connected"
                    )}
                  </p>
                  <p className="text-[12px] leading-5 text-neutral-500 dark:text-neutral-400">
                    {google.googleEmail !== null
                      ? "To use another Google account, disconnect this one first."
                      : "Google didn't name this account when it was connected. Disconnect and connect again to see it."}
                  </p>
                  <button
                    className={rbSecondaryButton}
                    data-cleo="google-disconnect"
                    disabled={googleControl.pending !== null}
                    onClick={() => onCommand({ type: "disconnectGoogle" })}
                    type="button"
                  >
                    {googleControl.pending === "disconnect" ? "Disconnecting…" : "Disconnect"}
                  </button>
                </>
              ) : google.status === "revoking" ? (
                <>
                  <p
                    className="text-[13px] text-neutral-500 dark:text-neutral-400"
                    data-cleo="google-revoking"
                  >
                    {google.revokeError === null
                      ? "Disconnecting from Google…"
                      : "Google hasn't confirmed the disconnect yet."}
                  </p>
                  {google.revokeError !== null ? (
                    <>
                      <p
                        className="text-[12px] leading-5 text-red-600 dark:text-red-400"
                        data-cleo="google-revoke-error"
                      >
                        {google.revokeError}
                      </p>
                      <button
                        className={rbSecondaryButton}
                        data-cleo="google-disconnect"
                        disabled={googleControl.pending !== null}
                        onClick={() => onCommand({ type: "disconnectGoogle" })}
                        type="button"
                      >
                        Try disconnecting again
                      </button>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <p
                    className="text-[13px] text-neutral-500 dark:text-neutral-400"
                    data-cleo={
                      google.status === "disconnected"
                        ? "google-disconnected"
                        : "google-unconnected"
                    }
                  >
                    {google.status === "disconnected"
                      ? "Disconnected from Google. Connect again with any account."
                      : "Not connected in this World."}
                  </p>
                  {google.status === "disconnected" && google.revokeError !== null ? (
                    <p
                      className="text-[12px] leading-5 text-red-600 dark:text-red-400"
                      data-cleo="google-unconfirmed"
                    >
                      Google didn't confirm the disconnect, so Neucleos forgot its token without
                      Google ending the access. To end it at Google too, remove Neucleos in{" "}
                      <a
                        className="underline underline-offset-2"
                        href="https://myaccount.google.com/connections"
                        rel="noreferrer"
                        target="_blank"
                      >
                        your Google Account's connections
                      </a>
                      .
                    </p>
                  ) : null}
                  {/* CN4: the begin rides the session grant through the door —
                      the browser only ever visits Google. */}
                  <button
                    className={rbAccentButton}
                    data-cleo="google-connect"
                    disabled={googleControl.pending !== null}
                    onClick={() => onCommand({ type: "connectGoogle" })}
                    type="button"
                  >
                    <Plus aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2} />
                    {googleControl.pending === "connect" ? "Opening Google…" : "Connect"}
                  </button>
                  {googleControl.connectStarted ? (
                    <p
                      className="text-[12px] leading-5 text-neutral-500 dark:text-neutral-400"
                      data-cleo="google-connect-pending"
                      role="status"
                    >
                      Finish in the Google window that opened, then come back here. If Google shows
                      a reason it didn't connect, that reason is the next step.
                    </p>
                  ) : null}
                  <a
                    className="text-[11px] text-neutral-500 underline-offset-2 hover:underline dark:text-neutral-400"
                    data-cleo="google-connect-fallback"
                    href={`${COCKPIT_CONNECT_ORIGIN}/connect/google?world=${encodeURIComponent(worldKey)}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    or connect via the cockpit
                    <ExternalLink
                      aria-hidden="true"
                      className="ml-1 inline h-3 w-3"
                      strokeWidth={2}
                    />
                  </a>
                </>
              )}
              {googleControl.error !== null ? (
                <p
                  className="text-[12px] leading-5 text-red-600 dark:text-red-400"
                  data-cleo="google-error"
                  role="alert"
                >
                  {googleControl.error}
                </p>
              ) : null}
            </div>
          )}
        </Card>

        <Card
          blurb="External calendars read in as colored sources, refreshed on a schedule by the authority."
          group="Calendar"
          integration="subscriptions"
          mark="ICS"
          name="Subscribed calendars"
        >
          {subscriptions.kind === "loading" ? (
            <p className="text-[13px] text-neutral-500 dark:text-neutral-400">Reading feeds…</p>
          ) : subscriptions.kind === "refusal" ? (
            <p
              className="text-[12px] leading-5 text-red-600 dark:text-red-400"
              data-cleo="integration-refusal"
            >
              {refusalLine(subscriptions.refusalKind, subscriptions.reason)}
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {feeds.length === 0 ? (
                <p className="text-[13px] text-neutral-500 dark:text-neutral-400">
                  No feeds subscribed yet.
                </p>
              ) : (
                feeds.map((feed) => (
                  <p
                    className="flex items-center gap-2 text-[12px]"
                    data-cleo="integration-feed"
                    data-feed-failed={feed.lastFetchError !== null ? "true" : undefined}
                    key={feed.subscriptionId}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                        feed.lastFetchError !== null ? "bg-red-500" : "bg-emerald-500"
                      }`}
                    />
                    <span className="min-w-0 truncate text-neutral-900 dark:text-white">
                      {feed.name}
                    </span>
                    {feed.lastFetchError !== null ? (
                      <span className="truncate text-red-600 dark:text-red-400">
                        {feed.lastFetchError}
                      </span>
                    ) : (
                      <span className="tabular-nums text-neutral-500 dark:text-neutral-400">
                        {agoLabel(feed.lastFetchAt)}
                      </span>
                    )}
                  </p>
                ))
              )}
              <button
                className={`${rbSecondaryButton} mt-1`}
                data-cleo="subscriptions-manage"
                onClick={() => onCommand({ type: "openCalendar" })}
                type="button"
              >
                Manage in Calendar
              </button>
            </div>
          )}
        </Card>

        <Card
          blurb="Read your connected Gmail accounts in Inbox."
          group="Communicator"
          integration="gmail"
          mark="GM"
          name="Gmail"
        >
          {gmail.kind === "loading" ? (
            <p className="text-[13px] text-neutral-500 dark:text-neutral-400">
              Checking connection…
            </p>
          ) : gmail.kind === "refusal" ? (
            <p
              className="text-[12px] leading-5 text-red-600 dark:text-red-400"
              data-cleo="integration-refusal"
            >
              {refusalLine(gmail.refusalKind, gmail.reason)}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {gmailAccounts.map((account) => (
                <div key={account.accountId} data-cleo="gmail-account">
                  <p
                    className={`flex items-center gap-1.5 text-[13px] ${account.status === "active" ? "text-emerald-600 dark:text-emerald-500" : "text-red-600 dark:text-red-400"}`}
                  >
                    {account.status === "active" ? (
                      <Check aria-hidden="true" className="h-3.5 w-3.5" />
                    ) : null}
                    {account.status === "active" ? "Connected" : "Reconnect required"}
                  </p>
                  <p className="truncate text-[12px] text-neutral-500 dark:text-neutral-400">
                    {account.address}
                  </p>
                </div>
              ))}
              {gmailAccounts.length === 0 ? (
                <p className="text-[13px] text-neutral-500 dark:text-neutral-400">
                  Not connected in this World.
                </p>
              ) : null}
              {!gmailConnected || gmailNeedsReconnect ? (
                <a
                  className={rbAccentButton}
                  data-cleo="gmail-connect-link"
                  href={`${COCKPIT_CONNECT_ORIGIN}/connect/gmail?world=${encodeURIComponent(worldKey)}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Plus aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2} />
                  {gmailNeedsReconnect ? "Reconnect" : "Connect"}
                  <ExternalLink aria-hidden="true" className="h-3 w-3" strokeWidth={2} />
                </a>
              ) : null}
            </div>
          )}
        </Card>

        <Card
          blurb="Publish a read-only feed of this calendar for your phone or any calendar app — the link is the whole authorization."
          group="Calendar"
          integration="feed"
          mark="OUT"
          name="Published feed"
        >
          <button
            className={rbSecondaryButton}
            data-cleo="feed-manage"
            onClick={() => onCommand({ type: "openCalendar" })}
            type="button"
          >
            Mint or revoke in Calendar
          </button>
        </Card>
      </div>
    </section>
  );
}

export function WorldIntegrations() {
  const worldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  return worldKey === null ? null : (
    <WorldIntegrationsForWorld key={worldKey} worldKey={worldKey} />
  );
}

function googleStateFrom(
  outcome: Awaited<ReturnType<typeof readGoogleCalendarStatus>>,
): IntegrationGoogleState {
  return outcome.ok
    ? {
        kind: "ready",
        status: outcome.status,
        googleSubject: outcome.googleSubject,
        googleEmail: outcome.googleEmail,
        scopes: outcome.scopes,
        revokeError: outcome.revokeError,
      }
    : { kind: "refusal", refusalKind: outcome.kind, reason: outcome.reason };
}

function WorldIntegrationsForWorld({ worldKey }: { readonly worldKey: string }) {
  const [google, setGoogle] = useState<IntegrationGoogleState>({ kind: "loading" });
  const [googleControl, setGoogleControl] = useState<IntegrationGoogleControl>(IDLE_GOOGLE_CONTROL);
  const [gmail, setGmail] = useState<IntegrationGmailState>({ kind: "loading" });
  const [subscriptions, setSubscriptions] = useState<IntegrationSubscriptionsState>({
    kind: "loading",
  });
  const [version, setVersion] = useState(0);
  const [revokePoll, setRevokePoll] = useState(0);

  useEffect(() => {
    if (worldKey === null) return;
    let alive = true;
    setGoogle({ kind: "loading" });
    setGmail({ kind: "loading" });
    setSubscriptions({ kind: "loading" });
    void readGoogleCalendarStatus(worldKey).then((outcome) => {
      if (!alive) return;
      setGoogle(googleStateFrom(outcome));
    });
    void listCalendarSubscriptions(worldKey).then((outcome) => {
      if (!alive) return;
      setSubscriptions(
        outcome.ok
          ? { kind: "ready", feeds: outcome.subscriptions }
          : { kind: "refusal", refusalKind: outcome.kind, reason: outcome.reason },
      );
    });
    void listEmailAccounts(worldKey).then((outcome) => {
      if (!alive) return;
      setGmail(
        outcome.ok
          ? { kind: "ready", accounts: outcome.accounts }
          : { kind: "refusal", refusalKind: outcome.kind, reason: outcome.reason },
      );
    });
    return () => {
      alive = false;
    };
  }, [worldKey, version]);

  // A connection that has landed retires the "finish in the browser" note.
  useEffect(() => {
    if (google.kind === "ready" && google.status === "active") {
      setGoogleControl((current) =>
        current.connectStarted ? { ...current, connectStarted: false } : current,
      );
    }
  }, [google]);

  // A Disconnect is "revoking" until Google confirms — usually a second.
  // The row re-reads only the Google status, in place, until it settles
  // or names the reason Google refused (7 Oct 2026: it sat on "in flight"
  // until the window next took focus). It keeps re-reading while a reason
  // is shown too: after "Try disconnecting again" the first read can still
  // carry the failure being retried (Codex, #1426), and the retry's success
  // must still land.
  const revoking = google.kind === "ready" && google.status === "revoking";
  useEffect(() => {
    if (!revoking || revokePoll >= GOOGLE_REVOKE_POLL_LIMIT) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      void readGoogleCalendarStatus(worldKey).then((outcome) => {
        if (!alive) return;
        setGoogle(googleStateFrom(outcome));
        setRevokePoll((current) => current + 1);
      });
    }, GOOGLE_REVOKE_POLL_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [revoking, revokePoll, worldKey]);

  useEffect(() => {
    // OAuth completes in another window; returning to Settings re-reads the
    // same status doors. No background polling or mailbox download.
    const refresh = () => setVersion((current) => current + 1);
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  const onCommand = useCallback(
    (command: WorldIntegrationsCommand) => {
      if (worldKey === null) return;
      switch (command.type) {
        case "refreshIntegrations": {
          setVersion((current) => current + 1);
          return;
        }
        case "openCalendar": {
          openWorldViewCalendar();
          return;
        }
        case "connectGoogle": {
          setGoogleControl({ pending: "connect", error: null, connectStarted: false });
          void beginGoogleCalendarConnect(
            worldKey,
            `${COCKPIT_CONNECT_ORIGIN}/api/google/callback`,
          ).then((outcome) => {
            if (!outcome.ok) {
              setGoogleControl({
                pending: null,
                error: connectRefusalLine("Calendar", outcome.kind, outcome.reason),
                connectStarted: false,
              });
              return;
            }
            window.open(outcome.authorizeUrl, "_blank", "noopener");
            setGoogleControl({ pending: null, error: null, connectStarted: true });
          });
          return;
        }
        case "disconnectGoogle": {
          setGoogleControl({ pending: "disconnect", error: null, connectStarted: false });
          void disconnectGoogleCalendar(worldKey).then((outcome) => {
            if (!outcome.ok) {
              setGoogleControl({
                pending: null,
                error: refusalLine(outcome.kind, outcome.reason),
                connectStarted: false,
              });
              return;
            }
            setGoogleControl(IDLE_GOOGLE_CONTROL);
            setRevokePoll(0);
            void readGoogleCalendarStatus(worldKey).then((status) =>
              setGoogle(googleStateFrom(status)),
            );
          });
          return;
        }
      }
    },
    [worldKey],
  );

  if (worldKey === null) return null;
  return (
    <WorldIntegrationsView
      google={google}
      googleControl={googleControl}
      gmail={gmail}
      onCommand={onCommand}
      subscriptions={subscriptions}
      worldKey={worldKey}
    />
  );
}
